import React from "react";
// Vee Rule Builder Modal

// Default
import { useState, useEffect } from "react";

// Mui
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";

// Custom
import ContainedButton from "../../forms/button/ContainedButton";
import CustomInput from "../../forms/input/Input";
import CustomOutlinedSelect from "../../forms/select/OutlinedCustomSelect";

const attributes = [
  {
    value: "entityList",
    label: "isEntity",
    type: "String",
    description: "Validate if a data field is entity",
  },
  {
    value: "valueThreshold",
    label: "thresholdVal",
    type: "number", // Numeric input
    description: "Validate if a field is within threshold",
  },
  {
    value: "callBackendLogic",
    label: "Call Backend Logic",
    type: "action", // Represents a backend call
    description: "Trigger backend logic",
  },
  {
    value: "callFlow",
    label: "Invoke another flow",
    type: "action", // Represents a backend call
    description: "Invoke anothjer flow",
  },
  {
    value: "enableLogging",
    label: "Enable Logging",
    type: "boolean", // Checkbox for true/false
    description: "Enable or disable logging for this block.",
  },
  {
    value: "timeout",
    label: "Timeout (ms)",
    type: "number",
    description: "Set a timeout in milliseconds for the operation.",
  },
  {
    value: "timestamp",
    label: "isTimestamp",
    type: "String",
    description: "Validate if a field is a dateTime stamp",
  },
  {
    value: "valueRange",
    label: "Value Range",
    type: "range",
    description: "Define acceptable minimum and maximum values.",
    example: { min: 0.0, max: 100.0 }, // Example structure for range
  },
  {
    value: "unit",
    label: "isUnit",
    type: "String",
    description: "Validate if a field is a unit",
  },
  {
    value: "notNull",
    label: "notNull",
    type: "String",
    description: "Validate if a field is not null",
  },
  {
    value: "jsonField",
    label: "Data field",
    type: "String",
    description: "Data field",
  },
  {
    value: "jsonFieldValue",
    label: "Data Value",
    type: "String",
    description: "Data Value",
  },
];

const VeeRuleBuilderModal = ({
  node,
  onSave,
  onClose,
  modalIsOpen,
  setToast,
}: {
  node: any;
  onSave: (node: any) => void;
  onClose: () => void;
  modalIsOpen: boolean;
  setToast: (toast: any) => void;
}) => {
  const isDataBlock = node?.type === "dataBlock";
  const [nodeName, setNodeName] = useState(
    node?.data?.ruleName || node?.data?.blockType || ""
  );
  const [nodeAttributes, setNodeAttributes] = useState([
    ...(node?.data?.attributes || []),
  ]);

  const [jsonData, setJsonData] = useState(
    JSON.stringify(node?.data?.jsonData || {}, null, 2)
  );

  const [newAttributeKey, setNewAttributeKey] = useState<any>({});

  useEffect(() => {
    setNodeName(node?.data?.ruleName || node?.data?.blockType || "");
    setNodeAttributes([...(node?.data?.attributes || [])]);
  }, [node]);

  const handleAddAttribute = () => {
    const attribute = attributes.find(
      (attr: any) => attr.value === newAttributeKey.value
    );
    if (attribute) {
      setNodeAttributes([...nodeAttributes, { ...attribute, value: "" }]);
      setNewAttributeKey("");
    }
  };

  const handleUpdateAttribute = (index: number, value: string) => {
    const updatedAttributes = [...nodeAttributes];
    updatedAttributes[index].value = value;
    setNodeAttributes(updatedAttributes);
  };

  const handleJsonChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setJsonData(e.target.value);
  };

  const handleSave = () => {
    if (isDataBlock) {
      // Validate JSON input before saving
      let parsedJson = {};
      try {
        parsedJson = JSON.parse(jsonData);
      } catch (err) {
        setToast({
          message: "Invalid JSON format. Please fix the JSON before saving.",
          severity: "error",
        });
        // alert("Invalid JSON format. Please fix the JSON before saving.");
        return;
      }
      onSave({ ...node, data: { ...node.data, jsonData: parsedJson } });
    } else {
      onSave({
        ...node,
        data: { ...node.data, ruleName: nodeName, attributes: nodeAttributes },
      });
    }
    onClose();
  };

  return (
    <Dialog
      onClose={onClose}
      open={modalIsOpen ? true : false}
      maxWidth={false}
      sx={{
        "& .MuiPaper-root": {
          width: "500px",
          maxWidth: "none",
        },
      }}
    >
      <Stack
        direction={"row"}
        justifyContent={"space-between"}
        alignItems={"center"}
      >
        <DialogTitle sx={{ m: 0, p: 2 }}>Edit Node Configuration</DialogTitle>
        <IconButton onClick={onClose} sx={{ marginRight: "10px" }}>
          <CloseIcon />
        </IconButton>
      </Stack>

      <DialogContent dividers>
        <CustomInput
          label={isDataBlock ? "Block Type:" : "Rule Name:"}
          value={nodeName}
          disabled={isDataBlock}
          onChange={(e) => setNodeName(e.target.value)}
        />

        {isDataBlock ? (
          <Stack mt={"20px"}>
            <CustomInput
              value={jsonData}
              label={"JSON Data"}
              onChange={handleJsonChange}
              multiline={true}
              rows={2}
              placeholder="Enter JSON data here.."
            />
          </Stack>
        ) : (
          <Stack>
            <Typography
              variant="body1"
              mt={"20px"}
              mb={"10px"}
              fontWeight={600}
            >
              Attributes
            </Typography>
            {nodeAttributes.map((attr, index) => (
              <Stack
                direction={"column"}
                gap={"20px"}
                alignItems={"center"}
                mb={"20px"}
              >
                {attr.type === "dropdown" ? (
                  <CustomOutlinedSelect
                    label={attr.label}
                    value={attr.value}
                    onChange={(e) =>
                      handleUpdateAttribute(index, e.value as string)
                    }
                    options={attr.options}
                  />
                ) : (
                  <CustomInput
                    label={attr.label}
                    type={attr.type}
                    value={attr.value}
                    onChange={(e) =>
                      handleUpdateAttribute(index, e.target.value)
                    }
                  />
                )}
              </Stack>
            ))}
            <Stack direction={"row"} gap={"20px"} alignItems={"center"}>
              <CustomOutlinedSelect
                value={newAttributeKey}
                onChange={(e) => setNewAttributeKey(e)}
                placeholder={"Select Attribute to Add"}
                options={attributes}
              />
              <ContainedButton
                label={"Add"}
                sx={{
                  width: "fit-content",
                  fontWeight: 600,
                  color: "#ffffff",
                  height: "39px",
                }}
                onClick={handleAddAttribute}
              />
            </Stack>
          </Stack>
        )}

        <DialogActions sx={{ padding: "20px 0 0 0" }}>
          <ContainedButton
            label={"Save"}
            sx={{
              width: "fit-content",
              fontWeight: 600,
              color: "#ffffff",
            }}
            onClick={handleSave}
          />
          <ContainedButton
            label={"Close"}
            bgColor={"linear-gradient(180deg, #FF7F7F 0%, #FF4D4D 100%)"}
            sx={{
              fontWeight: 600,
              color: "#ffffff",
            }}
            onClick={onClose}
          />
        </DialogActions>
      </DialogContent>
    </Dialog>
  );
};

export default VeeRuleBuilderModal;
